import CropFreeIcon from '@mui/icons-material/CropFree';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { CernQrCodeValue, RequestCheckAccess } from '../../../api/types';
import { AdamsError, getErrorMessage } from '../../../utils/errors';
import { cameraScanningErrorsNotToBeShown } from '../../../utils/utils';
import { DividerWithText } from '../../DividerWithText/DividerWithText';
import { ErrorMessage } from '../../ErrorMessage/ErrorMessage';
import { useHomeData } from '../../LoadHomeData/HomeDataProvider';
import { QRScanner } from '../../QRScanner/QRScanner';
import { EmulateAccessPointManualInput } from '../EmulateAccessPointManualInput';

interface EmulateAccessPointStep1Props {
    checkAccess: (arg: RequestCheckAccess) => void;
    loading: boolean;
    scanError: string | null;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setScanError: React.Dispatch<React.SetStateAction<string | null>>;
    translations: Record<string, string>;
}

export const EmulateAccessPointStep1: React.FC<EmulateAccessPointStep1Props> = ({
    checkAccess,
    loading,
    scanError,
    setLoading,
    setScanError,
    translations,
}) => {
    const { currentLanguage } = useHomeData();
    const [displayQRScanner, setDisplayQRScanner] = useState(false);

    const navigate = useNavigate();

    const handleScanDetect = (value: CernQrCodeValue) => {
        if (value) {
            const { accessPointCode } = value;
            setDisplayQRScanner(false);

            if (accessPointCode) {
                setLoading(true);
                setScanError(null);
                checkAccess({ language: currentLanguage, accessPointCode });
            } else {
                setScanError(translations.ERROR_NOT_ACP_QR_CODE);
            }
        } else {
            setScanError(translations.ERROR_NOT_CERN_QR_CODE);
        }
    };

    const handleScanError = (error: AdamsError) => {
        const errorMessage = getErrorMessage(error);
        if (errorMessage && !cameraScanningErrorsNotToBeShown.includes(errorMessage)) {
            setLoading(false);
            setDisplayQRScanner(false);
            setScanError(errorMessage);
        }
    };

    const handleClose = () => {
        setDisplayQRScanner(false);
        navigate('/');
    };

    return (
        <>
            {scanError && (
                <ErrorMessage
                    errorMessage={scanError}
                    showReturnButton={false}
                    title={translations.GLOBAL_ERROR_TITLE}
                    translations={translations}
                />
            )}
            {loading && (
                <Box mt={2} mb={4}>
                    <LinearProgress />
                </Box>
            )}

            {displayQRScanner && (
                <QRScanner
                    onDetect={handleScanDetect}
                    onError={handleScanError}
                    onClose={handleClose}
                    translations={translations}
                />
            )}

            <Box textAlign="center">
                <Box mt={2}>
                    <Typography variant="h6" gutterBottom>
                        {translations.EMULATE_ACCESS_POINT_SCAN_QR_BUTTON}
                    </Typography>
                </Box>

                <Box my={4}>
                    <Button
                        data-testid="start-scanning"
                        fullWidth
                        variant="contained"
                        color="primary"
                        size="large"
                        startIcon={<CropFreeIcon style={{ fontSize: '1rem' }} />}
                        onClick={() => setDisplayQRScanner(true)}
                    >
                        {translations.GLOBAL_SCAN_BUTTON}
                    </Button>
                </Box>
            </Box>

            <Box my={6}>
                <DividerWithText>{translations.GLOBAL_OR}</DividerWithText>
            </Box>

            <Box textAlign="center">
                <Typography variant="h6" gutterBottom>
                    {translations.CHECK_ACCESS_INPUT_LABEL}
                </Typography>
                <EmulateAccessPointManualInput
                    loading={loading}
                    onKeyDown={() => {
                        setScanError(null);
                    }}
                    onSuccess={(accessPointCode: string) => checkAccess({ language: currentLanguage, accessPointCode })}
                    onError={setScanError}
                    setLoading={setLoading}
                    translations={translations}
                />
            </Box>
        </>
    );
};
